import { useState, useId, useEffect, useMemo, useRef } from 'react';
import { format, parseISO, differenceInDays, addDays } from 'date-fns';
import { CalendarPlus, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useExtendBookingStay } from '@/hooks/api/useBookings';
import type { BookingEntry } from '@/services/api/frontdesk.api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function fmtCurrency(amount: number, currency = 'GBP'): string {
  if (isNaN(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ExtendStayModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingEntry;
}

export function ExtendStayModal({ isOpen, onClose, booking }: ExtendStayModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const newDateId = useId();

  const currentCheckOut = booking.checkOut;
  const minNewDate = format(addDays(parseISO(currentCheckOut), 1), 'yyyy-MM-dd');

  const [newCheckOut, setNewCheckOut] = useState(minNewDate);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const extendMutation = useExtendBookingStay();

  // Manage dialog open/close
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setNewCheckOut(minNewDate);
      setError('');
      setSuccessMessage('');
    }
  }, [isOpen, minNewDate]);

  // Calculations
  const additionalNights = useMemo(() => {
    try {
      const diff = differenceInDays(parseISO(newCheckOut), parseISO(currentCheckOut));
      return diff > 0 ? diff : 0;
    } catch {
      return 0;
    }
  }, [newCheckOut, currentCheckOut]);

  const nightlyRate = useMemo(() => {
    const total = parseFloat(booking.totalAmount) || 0;
    const originalNights = differenceInDays(parseISO(booking.checkOut), parseISO(booking.checkIn));
    return originalNights > 0 ? total / originalNights : 0;
  }, [booking]);

  const estimatedCharge = additionalNights * nightlyRate;

  const isValid = newCheckOut > currentCheckOut && additionalNights > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setError('');

    try {
      await extendMutation.mutateAsync({
        id: booking.id,
        payload: { newCheckOut },
      });
      setSuccessMessage('Stay extended successfully.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extend stay. The room may be booked for those dates.');
    }
  };

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="fixed inset-0 z-50 m-auto w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-200 p-0 backdrop:bg-black/40"
      aria-labelledby={headingId}
    >
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 id={headingId} className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CalendarPlus size={18} className="text-primary-600" aria-hidden="true" />
            Extend Stay
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="Close dialog"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Messages */}
          {successMessage && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-center gap-2" role="status" aria-live="polite">
              <CheckCircle2 size={16} className="text-green-600 shrink-0" aria-hidden="true" />
              <p className="text-sm font-medium text-green-700">{successMessage}</p>
            </div>
          )}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3" role="alert">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Guest & room info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-900">
              {booking.guest?.fullName ?? 'Unknown Guest'}
            </p>
            <p className="text-xs text-gray-500">
              {booking.unit?.name ?? booking.unitId.slice(0, 8)}
            </p>
          </div>

          {/* Current checkout date */}
          <div>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Current check-out:</span>{' '}
              {fmtDate(currentCheckOut)}
            </p>
          </div>

          {/* New checkout date picker */}
          <div>
            <label htmlFor={newDateId} className="block text-sm font-medium text-gray-700 mb-1">
              New Check-out Date <span className="text-gray-400 font-normal">(required)</span>
            </label>
            <input
              id={newDateId}
              type="date"
              value={newCheckOut}
              min={minNewDate}
              onChange={(e) => setNewCheckOut(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Additional nights & estimated charge */}
          {additionalNights > 0 && (
            <div className="bg-primary-50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Additional nights</span>
                <span className="font-semibold text-gray-900">{additionalNights}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Nightly rate</span>
                <span className="text-gray-700">{fmtCurrency(nightlyRate, booking.currency)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-primary-200 pt-2">
                <span className="font-semibold text-gray-900">Estimated additional charge</span>
                <span className="font-bold text-primary-700">{fmtCurrency(estimatedCharge, booking.currency)}</span>
              </div>
            </div>
          )}

          {/* Availability warning */}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-amber-700">
              Please verify room availability before confirming. If the room is booked
              after the current checkout, the extension may fail.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Cancel
          </button>
          <Button
            type="submit"
            isLoading={extendMutation.isPending}
            disabled={!isValid || extendMutation.isPending}
          >
            <CalendarPlus size={16} aria-hidden="true" />
            Confirm Extension
          </Button>
        </div>
      </form>
    </dialog>
  );
}
